// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Shared DOM/SVG helpers used by every render module.

export const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Create an SVG element in the correct namespace.
 *
 * @param {string} tag - SVG tag name, e.g. "g", "line", "text".
 * @param {Object} attrs - Key/value pairs forwarded verbatim to setAttribute.
 *   Callers pass hyphenated names ("stroke-width", "text-anchor") directly;
 *   this function does NOT camelCase-convert them.  Entries where the value is
 *   null or undefined are skipped so callers can use conditional spreading.
 * @param {Array<Node|string>} children - Child nodes or plain strings.
 *   Node instances are appended directly; strings become text nodes.
 * @returns {SVGElement}
 */
export function el(tag, attrs = {}, children = []) {
  const element = document.createElementNS(SVG_NS, tag);

  // Object.keys (not Object.entries) keeps this loop runnable on the very old
  // Chromium embedded by Tableau Desktop without any polyfill.
  for (const k of Object.keys(attrs)) {
    const v = attrs[k];
    if (v != null) {
      element.setAttribute(k, v);
    }
  }

  for (const child of children) {
    if (child instanceof Node) {
      element.appendChild(child);
    } else {
      element.appendChild(document.createTextNode(String(child)));
    }
  }

  return element;
}

/**
 * Convert a [r, g, b, a?] colour array to a CSS colour string.
 *
 * Rules:
 *   - a === 0                          -> "none"          (fully transparent)
 *   - ignoreAlpha || a === 255         -> "rgb(r, g, b)"  (opaque)
 *   - otherwise                        -> "rgba(r, g, b, a/255)"
 *
 * If colourArray is not an array (null, undefined, bad input) the function
 * returns "rgb(0,0,0)" as a safe fallback instead of throwing.
 *
 * @param {number[]} colourArray - [r, g, b] or [r, g, b, a].
 * @param {boolean} ignoreAlpha - When true, always emit rgb() form.
 * @returns {string}
 */
export function rgba(colourArray, ignoreAlpha = true) {
  if (!Array.isArray(colourArray)) return "rgb(0,0,0)";

  const [r, g, b, a = 255] = colourArray;

  if (a === 0) return "none";
  if (ignoreAlpha || a === 255) return `rgb(${r},${g},${b})`;
  return `rgba(${r},${g},${b},${(a / 255).toFixed(4)})`;
}

const ZERO_WIDTH_SPACE = "​";

/**
 * Build a native-title string with a zero-width-space suffix.
 *
 * Setting an element's `title` to a string it already holds does not re-trigger
 * the browser's native tooltip. Appending a varying number of invisible
 * zero-width spaces makes each successive title distinct, forcing a re-display
 * even when the visible text is unchanged. Falsy text clears the tooltip (returns "").
 *
 * @param {string|null|undefined} text - The visible tooltip text.
 * @param {number} [zwsCount=0] - How many zero-width spaces to append.
 * @returns {string}
 */
export function tooltipTitle(text, zwsCount = 0) {
  if (!text) return "";
  return `${text}${ZERO_WIDTH_SPACE.repeat(Math.max(0, Math.floor(zwsCount || 0)))}`;
}

/**
 * Remove all child nodes from a DOM node.
 *
 * @param {Node} node
 */
export function clear(node) {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }
}
